import { alpha, ListItem, ListItemProps, ListItemText, listItemTextClasses, styled } from '@chronosphereio/chrono-ui';
import React, { forwardRef, useCallback } from 'react';
import { LegendCheckbox } from './LegendCheckbox';
import { LegendIcon } from './LegendIcon';
import { LegendItem } from './legend-model';

// This value is hardcoded based on how tall the items are currently because trying to calculate
// them would be unnecessarily complex. However, this means we need to update this value if we
// ever change the layout of these items.
export const LIST_LEGEND_ITEM_HEIGHT = 20;

export type LegendItemEventOpts = {
  /**
   * Unique identifier for the legend item.
   */
  id: string;

  /**
   * Index of the row in the original data.
   */
  index: number;
};

export interface ListLegendItemProps
  extends Omit<ListItemProps<'div'>, 'onClick' | 'onMouseOver' | 'onMouseOut' | 'sx'> {
  item: LegendItem;

  index: number;

  /**
   * Whether or not this item is selected.
   */
  isSelected: boolean;

  /**
   * Handles user clicking on the entire legend item.
   */
  onClick: (e: React.MouseEvent<HTMLElement, MouseEvent>, seriesId: string) => void;
  /**
   * Handles user clicking on the checkbox within the legend item.
   */
  onCheckboxChange: (seriesId: string) => void;

  onMouseOver?: (e: React.MouseEvent, opts: LegendItemEventOpts) => void;
  onMouseOut?: (e: React.MouseEvent, opts: LegendItemEventOpts) => void;
  disableListLegendItemTruncation?: boolean;
}

const ListLegendItemBase = forwardRef<HTMLDivElement, ListLegendItemProps>(function ListLegendItem(
  {
    item,
    isSelected,
    onClick,
    onCheckboxChange,
    onMouseOver,
    onMouseOut,
    index,
    disableListLegendItemTruncation,
    ...others
  },
  ref
) {
  const handleClick: React.MouseEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      onClick(e, item.id);
      item.onClick?.(e);
    },
    [item, onClick]
  );

  const handleCheckboxChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      e.stopPropagation();
      onCheckboxChange(item.id);
    },
    [item.id, onCheckboxChange]
  );

  return (
    <LegendListItem
      {...others}
      component="div"
      role="listitem"
      dense={true}
      key={item.id}
      onClick={handleClick}
      onMouseOver={(e: React.MouseEvent) => onMouseOver?.(e, { id: item.id, index })}
      onMouseOut={(e: React.MouseEvent) => onMouseOut?.(e, { id: item.id, index })}
      ref={ref}
    >
      <LegendCheckbox
        checked={isSelected}
        color={item.color}
        variant={item.isComparison ? 'striped' : 'solid'}
        onChange={handleCheckboxChange}
      />
      <LegendIcon icon={item.icon} mode="List" />
      <ListItemText
        primary={item.label}
        primaryTypographyProps={{
          noWrap: !disableListLegendItemTruncation,
          title: item.label,
          paddingY: disableListLegendItemTruncation ? 0.25 : 0,
        }}
      />
    </LegendListItem>
  );
});

export const ListLegendItem = React.memo(ListLegendItemBase);

const LegendListItem = styled(ListItem)(({ theme }) => {
  const hoverBackgroundColor = theme.featureFlags.panwRebrandStubYear
    ? theme.palette.background.hover
    : alpha(theme.palette.primary.main, 0.1);

  return {
    padding: 0,
    cursor: 'pointer',
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.25),

    '&:hover': {
      backgroundColor: hoverBackgroundColor,
    },

    [`.${listItemTextClasses.root}`]: {
      marginTop: 0,
      marginBottom: 0,
    },
  };

  // Type casting needed because of the `component` prop, styled, and typescript
  // not playing nicely together.
  // https://mui.com/material-ui/guides/typescript/#complications-with-the-component-prop
}) as unknown as typeof ListItem;
